// src/sim/rng.ts — seeded RNG sub-tree (D-19).
// Source: Phase 0 RESEARCH.md §Pattern 4 (Alea Sub-Seed Tree).
//
// Banned-in-sim (per Plan 02 ESLint): Math.random, Date.now,
// performance.now, setTimeout, setInterval, new Date().
// This file holds the ONLY approved randomness source for src/sim/**.

import Alea from 'alea';

export interface SimRng {
  ai: () => number;
  dogs: () => number;
  waffle: () => number;
  hiding: () => number;
  motionLights: () => number;
  /** P3 D-10 — round FSM / team-switch jitter. */
  round: () => number;
  /** P3 D-10 / D-17 — foxguard shouter selection (RNG-picked caught hider). */
  foxguard: () => number;
}

/**
 * djb2-style deterministic hash of (rootSeed, label).
 * Produces an unsigned 32-bit integer suitable as an alea seed.
 *
 * `Math.imul` overflow is ECMAScript-spec defined, so this is stable
 * across engines (V8, JavaScriptCore, SpiderMonkey).
 */
function hashSeed(root: number, label: string): number {
  let h = root >>> 0;
  for (let i = 0; i < label.length; i++) {
    h = (Math.imul(31, h) + label.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/**
 * Build seven named alea streams from a single root seed.
 *
 * Subsystem boundaries (D-19) — changing one subsystem's RNG draws
 * must not affect another subsystem's stream. Adding a new subsystem
 * requires adding a new label here AND updating the SimRng interface.
 */
export function createRng(rootSeed: number): SimRng {
  return {
    ai: Alea(hashSeed(rootSeed, 'ai')),
    dogs: Alea(hashSeed(rootSeed, 'dogs')),
    waffle: Alea(hashSeed(rootSeed, 'waffle')),
    hiding: Alea(hashSeed(rootSeed, 'hiding')),
    motionLights: Alea(hashSeed(rootSeed, 'motion-lights')),
    round: Alea(hashSeed(rootSeed, 'round')),
    foxguard: Alea(hashSeed(rootSeed, 'foxguard')),
  };
}

// Exported so the golden-hash test can cross-check hashSeed determinism
// if CI ever claims hash drift between engines.
export { hashSeed };
